import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { api, type CreditAssignment, type CreditEntry, type CreditSave } from './api';
import { Badge, Card, CardHeader, Kpi, SearchBar, Spinner } from './components/Ui';
import { UnifiedTimeFilter } from './components/UnifiedTimeFilter';
import { useTimeFilter } from './context/TimeFilterContext';
import { decimalExact, money, moneyCompact, shortDate } from './format';
import { useAsyncData } from './hooks/useAsyncData';
import { Shell, type ShellProps } from './Shell';

/* ── Types ─────────────────────────────────────────────────────────── */

interface RecruiterOption {
  readonly id: string;
  readonly fullName: string;
  readonly role: string;
}

type SortKey =
  | 'worker'
  | 'billRate'
  | 'payRate'
  | 'periodGp'
  | 'recruiter'
  | 'recPct'
  | 'salesperson'
  | 'salesPct';
type SortDir = 'asc' | 'desc';

interface EditingCell {
  assignmentId: string;
  lane: 'recruiting' | 'sales';
  idx: number;
  value: string;
}

interface StructuralEdit {
  assignmentId: string;
  recruiting: { userId: string; share: string; userName: string }[];
  sales: { userId: string; share: string; userName: string }[];
}

/* ── Column group background classes ──────────────────────────────── */
const G = {
  contractor: 'bg-slate-100/70',
  rates: '',
  recruiter: 'bg-slate-100/70',
  sales: '',
} as const;

/* ── Helpers ────────────────────────────────────────────────────────── */

function pct(share: string): number {
  return Math.round(Number(share) * 100);
}

function laneTotal(entries: { share: string }[]): number {
  return entries.reduce((sum, e) => sum + Number(e.share), 0);
}

function isNonStandard(entries: { share: string }[]): boolean {
  const t = laneTotal(entries);
  return entries.length > 0 && Math.abs(t - 1) > 0.001;
}

function firstName(entries: CreditEntry[]): string {
  return entries[0]?.userName ?? '';
}

function firstPct(entries: CreditEntry[]): number {
  return entries[0] ? pct(entries[0].share) : 0;
}

function num(val: string | null): number {
  return val ? Number(val) || 0 : 0;
}

function sortAssignments(list: CreditAssignment[], key: SortKey, dir: SortDir): CreditAssignment[] {
  const sorted = [...list];
  const m = dir === 'asc' ? 1 : -1;
  sorted.sort((a, b) => {
    let cmp = 0;
    switch (key) {
      case 'worker':
        cmp = a.workerName.localeCompare(b.workerName);
        break;
      case 'billRate':
        cmp = num(a.billRate) - num(b.billRate);
        break;
      case 'payRate':
        cmp = num(a.payRate) - num(b.payRate);
        break;
      case 'periodGp':
        cmp = num(a.periodGp) - num(b.periodGp);
        break;
      case 'recruiter':
        cmp = firstName(a.recruiting).localeCompare(firstName(b.recruiting));
        break;
      case 'recPct':
        cmp = firstPct(a.recruiting) - firstPct(b.recruiting);
        break;
      case 'salesperson':
        cmp = firstName(a.sales).localeCompare(firstName(b.sales));
        break;
      case 'salesPct':
        cmp = firstPct(a.sales) - firstPct(b.sales);
        break;
    }
    return cmp * m;
  });
  return sorted;
}

function toCreditSaves(
  a: CreditAssignment,
  patch?: {
    lane: 'recruiting' | 'sales';
    idx: number;
    share?: string;
    commissionPlanId?: string | null;
  },
): CreditSave[] {
  const mapLane = (lane: 'recruiting' | 'sales', entries: CreditEntry[]): CreditSave[] =>
    entries
      .filter((c) => c.userId)
      .map((c, i) => {
        const isTarget = patch?.lane === lane && patch.idx === i;
        return {
          userId: c.userId,
          share: isTarget && patch.share !== undefined ? patch.share : c.share,
          lane,
          commissionPlanId:
            isTarget && patch.commissionPlanId !== undefined
              ? patch.commissionPlanId
              : c.commissionPlanId,
        };
      });
  return [...mapLane('recruiting', a.recruiting), ...mapLane('sales', a.sales)];
}

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

/** Format the GP derivation formula: ($bill − $pay × LF) × hours */
function gpFormula(a: CreditAssignment): string {
  return `(${money(a.billRate)} − ${money(a.payRate)} × ${decimalExact(a.loadFactor)}) × ${decimalExact(a.periodHours)}h`;
}

/* ── Column definitions ─────────────────────────────────────────── */

interface Col {
  label: string;
  sortKey: SortKey;
  align: 'left' | 'right' | 'center';
  group: keyof typeof G;
  superGroup?: 'financials' | 'recruiting' | 'sales';
}

/** Left-border at group boundaries */
const SEP_BORDER = 'border-l-2 border-l-slate-300/60';

const COLUMNS: Col[] = [
  { label: 'Contractor', sortKey: 'worker', align: 'left', group: 'contractor' },
  { label: 'Bill', sortKey: 'billRate', align: 'right', group: 'rates', superGroup: 'financials' },
  { label: 'Pay', sortKey: 'payRate', align: 'right', group: 'rates', superGroup: 'financials' },
  { label: 'GP', sortKey: 'periodGp', align: 'right', group: 'rates', superGroup: 'financials' },
  {
    label: 'Recruiter',
    sortKey: 'recruiter',
    align: 'left',
    group: 'recruiter',
    superGroup: 'recruiting',
  },
  {
    label: 'Plan',
    sortKey: 'recruiter',
    align: 'left',
    group: 'recruiter',
    superGroup: 'recruiting',
  },
  {
    label: 'Rec. %',
    sortKey: 'recPct',
    align: 'right',
    group: 'recruiter',
    superGroup: 'recruiting',
  },
  {
    label: 'Salesperson',
    sortKey: 'salesperson',
    align: 'left',
    group: 'sales',
    superGroup: 'sales',
  },
  { label: 'Plan', sortKey: 'salesperson', align: 'left', group: 'sales', superGroup: 'sales' },
  { label: 'Sales %', sortKey: 'salesPct', align: 'right', group: 'sales', superGroup: 'sales' },
];

const COL_COUNT = COLUMNS.length;

function alignClass(a: 'left' | 'right' | 'center'): string {
  if (a === 'right') return 'text-right';
  if (a === 'center') return 'text-center';
  return 'text-left';
}

/* ── Component ──────────────────────────────────────────────────────── */

export function CommissionCredits(props: ShellProps) {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const [sortKey, setSortKey] = useState<SortKey>('worker');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const [structuralEdit, setStructuralEdit] = useState<StructuralEdit | null>(null);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [weekScope, setWeekScope] = useState<'1' | '2' | 'both'>('both');
  const tf = useTimeFilter();

  /* ── Filters ─────────────────────────────────────────────────── */
  const [contractorFilter, setContractorFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [recruiterFilter, setRecruiterFilter] = useState('');
  const hasFilters = contractorFilter !== '' || clientFilter !== '' || recruiterFilter !== '';

  const { data, error, loading, reload } = useAsyncData(
    () =>
      Promise.all([
        api.getCommissionCredits(
          debouncedSearch || undefined,
          tf.toPeriodId(),
          undefined,
          weekScope,
        ),
        api.listAvailableRecruiters(),
        api.getCommissionPlanOptions(),
      ]).then(([credits, recruiters, plans]) => ({
        ...credits,
        recruiters,
        plans: plans.plans,
      })),
    [debouncedSearch, weekScope, tf.selection],
    'Could not load commission credits.',
  );

  const recruiterOptions: RecruiterOption[] = useMemo(
    () =>
      data?.recruiters?.map((r: { id: string; full_name: string; role: string }) => ({
        id: r.id,
        fullName: r.full_name,
        role: r.role,
      })) ?? [],
    [data?.recruiters],
  );
  const recruitersOnly = useMemo(
    () => recruiterOptions.filter((r) => r.role === 'recruiter'),
    [recruiterOptions],
  );
  const salespeopleOnly = useMemo(
    () => recruiterOptions.filter((r) => r.role === 'salesperson'),
    [recruiterOptions],
  );

  const handleSort = useCallback(
    (key: string) => {
      const k = key as SortKey;
      if (k === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      else {
        setSortKey(k);
        setSortDir('asc');
      }
    },
    [sortKey],
  );

  /* ── Inline percentage edit ──────────────────────────────────── */

  const startCellEdit = useCallback(
    (assignmentId: string, lane: 'recruiting' | 'sales', idx: number, currentShare: string) => {
      setEditingCell({ assignmentId, lane, idx, value: String(pct(currentShare)) });
      setActionError(null);
    },
    [],
  );

  const cancelCellEdit = useCallback(() => setEditingCell(null), []);

  const saveCellEdit = useCallback(async () => {
    if (!editingCell || !data) return;
    const assignment = (data.assignments as CreditAssignment[]).find(
      (a) => a.assignmentId === editingCell.assignmentId,
    );
    if (!assignment) return;

    const newShare = String(Number(editingCell.value) / 100);
    const credits = toCreditSaves(assignment, {
      lane: editingCell.lane,
      idx: editingCell.idx,
      share: newShare,
    });

    setSaving(true);
    setActionError(null);
    try {
      await api.saveCommissionCredits(editingCell.assignmentId, credits);
      setEditingCell(null);
      reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  }, [editingCell, data, reload]);

  /* ── Structural edit ─────────────────────────────────────────── */

  const startStructuralEdit = useCallback((a: CreditAssignment) => {
    setStructuralEdit({
      assignmentId: a.assignmentId,
      recruiting: a.recruiting.map((c) => ({
        userId: c.userId,
        share: c.share,
        userName: c.userName,
      })),
      sales: a.sales.map((c) => ({
        userId: c.userId,
        share: c.share,
        userName: c.userName,
      })),
    });
    setEditingCell(null);
    setActionError(null);
  }, []);

  const cancelStructuralEdit = useCallback(() => {
    setStructuralEdit(null);
    setActionError(null);
  }, []);

  const saveEngagementStart = useCallback(
    async (assignmentId: string, date: string) => {
      setSaving(true);
      setActionError(null);
      try {
        await api.updateAssignmentStart(assignmentId, date);
        reload();
      } catch (e) {
        setActionError(e instanceof Error ? e.message : 'Save failed.');
      } finally {
        setSaving(false);
      }
    },
    [reload],
  );

  const saveLaneField = useCallback(
    async (
      assignment: CreditAssignment,
      lane: 'recruiting' | 'sales',
      idx: number,
      field: 'commissionPlanId',
      value: string,
    ) => {
      setSaving(true);
      setActionError(null);
      try {
        await api.saveCommissionCredits(
          assignment.assignmentId,
          toCreditSaves(assignment, { lane, idx, [field]: value || null }),
        );
        reload();
      } catch (e) {
        setActionError(e instanceof Error ? e.message : 'Save failed.');
      } finally {
        setSaving(false);
      }
    },
    [reload],
  );

  const saveStructuralEdit = useCallback(async () => {
    if (!structuralEdit || !data) return;
    const original = (data.assignments as CreditAssignment[]).find(
      (a) => a.assignmentId === structuralEdit.assignmentId,
    );
    const credits: CreditSave[] = [
      ...structuralEdit.recruiting
        .filter((c) => c.userId)
        .map((c, i) => ({
          userId: c.userId,
          share: c.share,
          lane: 'recruiting' as const,
          commissionPlanId: original?.recruiting[i]?.commissionPlanId ?? null,
        })),
      ...structuralEdit.sales
        .filter((c) => c.userId)
        .map((c, i) => ({
          userId: c.userId,
          share: c.share,
          lane: 'sales' as const,
          commissionPlanId: original?.sales[i]?.commissionPlanId ?? null,
        })),
    ];
    setSaving(true);
    setActionError(null);
    try {
      await api.saveCommissionCredits(structuralEdit.assignmentId, credits);
      setStructuralEdit(null);
      reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  }, [structuralEdit, data, reload]);

  /* ── Render ──────────────────────────────────────────────────── */

  const hasData = data !== null;
  const assignments: CreditAssignment[] = hasData ? data.assignments : [];
  const sorted = useMemo(
    () => sortAssignments(assignments, sortKey, sortDir),
    [assignments, sortKey, sortDir],
  );

  /* Client-side filters */
  const filtered = useMemo(() => {
    let result = sorted;
    if (contractorFilter) result = result.filter((a) => a.workerName === contractorFilter);
    if (clientFilter) result = result.filter((a) => a.clientName === clientFilter);
    if (recruiterFilter)
      result = result.filter((a) => a.recruiting.some((r) => r.userId === recruiterFilter));
    return result;
  }, [sorted, contractorFilter, clientFilter, recruiterFilter]);

  /* Filter option lists */
  const contractorNames = useMemo(
    () => [...new Set(assignments.map((a) => a.workerName))].sort(),
    [assignments],
  );
  const clientNames = useMemo(
    () => [...new Set(assignments.map((a) => a.clientName))].sort(),
    [assignments],
  );

  /* KPI aggregations */
  const totalGp = useMemo(
    () => assignments.reduce((s, a) => s + num(a.periodGp), 0),
    [assignments],
  );
  const totalHours = useMemo(
    () => assignments.reduce((s, a) => s + num(a.periodHours), 0),
    [assignments],
  );
  const splitWarnings = useMemo(
    () => assignments.filter((a) => isNonStandard(a.recruiting) || isNonStandard(a.sales)).length,
    [assignments],
  );

  const clearFilters = useCallback(() => {
    setContractorFilter('');
    setClientFilter('');
    setRecruiterFilter('');
  }, []);

  if (error !== null && !hasData) {
    return (
      <Shell {...props}>
        <Card className="px-5 py-8">
          <p className="text-sm text-caution">{error}</p>
        </Card>
      </Shell>
    );
  }

  return (
    <Shell {...props}>
      <div className="flex h-[calc(100vh-3rem)] flex-col">
        {/* ── Page header ── */}
        <div className="relative z-40 mb-4 flex shrink-0 items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-ink">Commission Credits</h1>
            <p className="mt-1 text-sm text-ink-faint">
              Manage recruiter and salesperson credit allocations
            </p>
          </div>
          <UnifiedTimeFilter />
        </div>

        {/* ── KPI strip ── */}
        {hasData && (
          <div className="mb-4 shrink-0 grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Kpi
              label="Active assignments"
              value={String(assignments.length)}
              hint={`${String(filtered.length)} shown after filters`}
              delay={0}
            />
            <Kpi
              label="Period GP"
              value={moneyCompact(totalGp.toFixed(4))}
              exact={money(totalGp.toFixed(4))}
              tone="profit"
              delay={50}
            />
            <Kpi
              label="Total hours"
              value={totalHours.toFixed(1).replace(/\.0$/, '')}
              delay={100}
            />
            <Kpi
              label="Split warnings"
              value={String(splitWarnings)}
              hint={splitWarnings > 0 ? 'Shares do not total 100%' : 'All allocations balanced'}
              tone={splitWarnings > 0 ? 'caution' : 'neutral'}
              delay={150}
            />
          </div>
        )}

        {/* ── Table card ── */}
        <Card className="flex min-h-0 flex-1 flex-col overflow-hidden" delay={200}>
          <div className="shrink-0">
            <CardHeader
              title=""
              aside={
                <div className="flex w-full items-center gap-3">
                  {/* Filters */}
                  <FilterSelect
                    label="Contractor"
                    value={contractorFilter}
                    options={contractorNames}
                    onChange={setContractorFilter}
                  />
                  <FilterSelect
                    label="Client"
                    value={clientFilter}
                    options={clientNames}
                    onChange={setClientFilter}
                  />
                  <FilterSelect
                    label="Recruiter"
                    value={recruiterFilter}
                    options={recruitersOnly.map((r) => r.id)}
                    labels={recruitersOnly.reduce(
                      (acc, r) => {
                        acc[r.id] = r.fullName;
                        return acc;
                      },
                      {} as Record<string, string>,
                    )}
                    onChange={setRecruiterFilter}
                  />
                  {hasFilters && (
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="text-xs text-profit hover:underline"
                    >
                      Clear
                    </button>
                  )}

                  <div className="ml-auto flex items-center gap-3">
                    {loading && hasData && (
                      <span className="h-2 w-2 animate-pulse rounded-full bg-revenue-dim" />
                    )}
                    <div className="flex overflow-hidden rounded-lg border border-edge">
                      {(['1', '2', 'both'] as const).map((scope) => {
                        const week = data?.weeks?.[scope === '1' ? 0 : 1];
                        const label =
                          scope === 'both'
                            ? 'Both weeks'
                            : week
                              ? `${week.label}`
                              : `Week ${scope}`;
                        return (
                          <button
                            key={scope}
                            type="button"
                            className={`px-3 py-1 text-xs font-medium ${
                              weekScope === scope
                                ? 'bg-profit text-white'
                                : 'bg-surface-base text-ink-muted hover:bg-surface-overlay'
                            }`}
                            onClick={() => setWeekScope(scope)}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                    <div className="w-64">
                      <SearchBar
                        value={search}
                        onChange={setSearch}
                        placeholder="Search contractor, recruiter, or client…"
                      />
                    </div>
                  </div>
                </div>
              }
            />

            {actionError && (
              <div className="mx-4 mb-3 rounded-lg border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-800">
                {actionError}
              </div>
            )}
          </div>

          {/* Scrollable table body */}
          {!hasData && loading ? (
            <Spinner label="Loading commission credits…" />
          ) : filtered.length > 0 ? (
            <div className="min-h-0 flex-1 overflow-auto">
              <table className="w-full border-collapse text-sm">
                <thead className="sticky top-0 z-10">
                  {/* ── Super-headers ── */}
                  <tr className="bg-surface-base">
                    <th className={`py-1 ${G.contractor}`} />
                    <th
                      colSpan={3}
                      className="py-1 text-center text-[10px] font-semibold uppercase tracking-[0.08em] text-ink-faint"
                    >
                      Financials
                    </th>
                    <th
                      colSpan={3}
                      className={`py-1 text-center text-[10px] font-semibold uppercase tracking-[0.08em] text-ink-faint ${G.recruiter} ${SEP_BORDER}`}
                    >
                      Recruiting
                    </th>
                    <th
                      colSpan={3}
                      className={`py-1 text-center text-[10px] font-semibold uppercase tracking-[0.08em] text-ink-faint ${SEP_BORDER}`}
                    >
                      Sales
                    </th>
                  </tr>
                  {/* ── Sort headers ── */}
                  <tr className="border-b border-edge bg-surface-base shadow-sm">
                    {COLUMNS.map((col, ci) => {
                      const isGroupStart =
                        ci === 0 || (ci > 0 && col.superGroup !== COLUMNS[ci - 1]!.superGroup);
                      return (
                        <th
                          key={`${col.sortKey}-${String(ci)}`}
                          scope="col"
                          className={`px-3 py-2.5 text-[11px] font-bold uppercase tracking-[0.06em] text-ink-muted cursor-pointer select-none hover:text-ink-default ${alignClass(col.align)} ${G[col.group]} ${isGroupStart && ci > 0 ? SEP_BORDER : ''}`}
                          onClick={() => handleSort(col.sortKey)}
                        >
                          <span className="inline-flex items-center gap-1">
                            {col.label}
                            {sortKey === col.sortKey && (
                              <span className="text-profit">{sortDir === 'asc' ? '↑' : '↓'}</span>
                            )}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((a) => {
                    if (structuralEdit?.assignmentId === a.assignmentId) {
                      return (
                        <StructuralEditRows
                          key={a.assignmentId}
                          assignment={a}
                          draft={structuralEdit}
                          setDraft={setStructuralEdit}
                          recruitersOnly={recruitersOnly}
                          salespeopleOnly={salespeopleOnly}
                          saving={saving}
                          onSave={saveStructuralEdit}
                          onCancel={cancelStructuralEdit}
                        />
                      );
                    }
                    return (
                      <AssignmentRows
                        key={a.assignmentId}
                        assignment={a}
                        editingCell={editingCell}
                        saving={saving}
                        plans={data?.plans ?? []}
                        navigate={props.navigate}
                        onCellClick={startCellEdit}
                        onCellChange={(v) =>
                          setEditingCell((prev) => (prev ? { ...prev, value: v } : null))
                        }
                        onCellSave={saveCellEdit}
                        onCellCancel={cancelCellEdit}
                        onManage={() => startStructuralEdit(a)}
                        onPlanChange={(lane, idx, planId) =>
                          void saveLaneField(a, lane, idx, 'commissionPlanId', planId)
                        }
                        onEngagementStartChange={(date) =>
                          void saveEngagementStart(a.assignmentId, date)
                        }
                      />
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="px-4 py-6 text-sm text-ink-muted">
              {hasFilters
                ? 'No assignments match the current filters.'
                : 'No active assignments found.'}
            </p>
          )}
        </Card>
      </div>
    </Shell>
  );
}

/* ── Filter select ──────────────────────────────────────────────────── */

function FilterSelect({
  label,
  value,
  options,
  labels,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  labels?: Record<string, string>;
  onChange: (v: string) => void;
}) {
  return (
    <select
      className={`rounded-lg border px-2.5 py-1 text-xs font-medium transition-colors ${
        value
          ? 'border-profit bg-profit/5 text-profit'
          : 'border-edge bg-surface-base text-ink-muted hover:border-edge-strong'
      }`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">{label}</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {labels ? (labels[opt] ?? opt) : opt}
        </option>
      ))}
    </select>
  );
}

/* ── Cell helper with group background ──────────────────────────── */

function Td({
  group,
  align = 'left',
  mono = false,
  sep = false,
  className: extraClass = '',
  children,
}: {
  group: keyof typeof G;
  align?: 'left' | 'right' | 'center';
  mono?: boolean;
  sep?: boolean;
  className?: string;
  children?: React.ReactNode;
}) {
  return (
    <td
      className={`px-3 py-2 align-top ${alignClass(align)} ${G[group]} ${mono ? 'font-mono' : ''} text-ink-muted ${sep ? SEP_BORDER : ''} ${extraClass}`}
    >
      {children}
    </td>
  );
}

/* ── Assignment rows (normal mode) ──────────────────────────────── */

function AssignmentRows({
  assignment: a,
  editingCell,
  saving,
  plans,
  navigate,
  onCellClick,
  onCellChange,
  onCellSave,
  onCellCancel,
  onManage,
  onPlanChange,
  onEngagementStartChange,
}: {
  assignment: CreditAssignment;
  editingCell: EditingCell | null;
  saving: boolean;
  plans: { id: string; name: string; elevatedPeriodMonths: number }[];
  navigate: (hash: string) => void;
  onCellClick: (id: string, lane: 'recruiting' | 'sales', idx: number, share: string) => void;
  onCellChange: (value: string) => void;
  onCellSave: () => void;
  onCellCancel: () => void;
  onManage: () => void;
  onPlanChange: (lane: 'recruiting' | 'sales', idx: number, planId: string) => void;
  onEngagementStartChange: (date: string) => void;
}) {
  const maxLen = Math.max(a.recruiting.length, a.sales.length, 1);
  const recWarning = isNonStandard(a.recruiting);
  const salesWarning = isNonStandard(a.sales);
  const engStartRef = useRef<HTMLInputElement>(null);
  const engStartFormatted = a.engagementStart ? shortDate(a.engagementStart.slice(0, 10)) : null;

  return (
    <>
      {Array.from({ length: maxLen }, (_, i) => {
        const rec = a.recruiting[i];
        const sal = a.sales[i];
        const isFirst = i === 0;

        const isEditingRec =
          editingCell?.assignmentId === a.assignmentId &&
          editingCell.lane === 'recruiting' &&
          editingCell.idx === i;
        const isEditingSal =
          editingCell?.assignmentId === a.assignmentId &&
          editingCell.lane === 'sales' &&
          editingCell.idx === i;

        return (
          <tr
            key={`${a.assignmentId}-${String(i)}`}
            className="border-b border-edge/50 transition-colors last:border-0 hover:bg-surface-overlay/60"
          >
            {/* ── Contractor (name + client) ── */}
            <td
              className={`sticky left-0 z-[5] px-3 py-2 align-top bg-surface-raised ${G.contractor}`}
            >
              {isFirst ? (
                <div className="flex items-start gap-1.5">
                  <button
                    type="button"
                    className="mt-0.5 inline-flex shrink-0 items-center justify-center rounded p-0.5 text-ink-faint transition-colors hover:bg-profit/10 hover:text-profit"
                    onClick={onManage}
                    title="Add / remove people"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    >
                      <circle cx="6" cy="6" r="3" />
                      <path d="M12 14c0-2.2-2.7-4-6-4s-6 1.8-6 4" />
                      <path d="M13 4v4M11 6h4" />
                    </svg>
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        className="text-ink-muted hover:text-profit hover:underline"
                        onClick={() => navigate(`contractor/${a.workerId}`)}
                      >
                        {a.workerName}
                      </button>
                      {recWarning && (
                        <Badge tone="warning">
                          Rec {String(Math.round(laneTotal(a.recruiting) * 100))}%
                        </Badge>
                      )}
                      {salesWarning && (
                        <Badge tone="warning">
                          Sales {String(Math.round(laneTotal(a.sales) * 100))}%
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-start justify-between gap-2">
                      <span className="min-w-0 truncate text-[11px] text-ink-faint">
                        {a.clientName}
                      </span>
                      <span className="shrink-0 flex flex-col items-end">
                        <span className="text-[9px] font-semibold uppercase tracking-[0.06em] text-ink-faint">
                          Start Date
                        </span>
                        {engStartFormatted ? (
                          <button
                            type="button"
                            className="text-[11px] font-mono text-ink-faint hover:text-profit"
                            title="Engagement start — when the contractor began this assignment. Click to change."
                            onClick={() => engStartRef.current?.showPicker()}
                          >
                            {engStartFormatted}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="text-[11px] italic text-ink-faint hover:text-profit"
                            title="Set engagement start date"
                            onClick={() => engStartRef.current?.showPicker()}
                          >
                            No start
                          </button>
                        )}
                        <input
                          ref={engStartRef}
                          type="date"
                          className="invisible absolute h-0 w-0"
                          value={a.engagementStart?.slice(0, 10) ?? ''}
                          disabled={saving}
                          onChange={(e) => {
                            if (e.target.value) onEngagementStartChange(e.target.value);
                          }}
                        />
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                ''
              )}
            </td>

            {/* ── Financials: Bill ── */}
            <Td group="rates" align="right" mono sep>
              {isFirst ? money(a.billRate) : ''}
            </Td>

            {/* ── Financials: Pay ── */}
            <Td group="rates" align="right" mono>
              {isFirst ? money(a.payRate) : ''}
            </Td>

            {/* ── Financials: GP (value + formula subtitle) ── */}
            <Td group="rates" align="right">
              {isFirst ? (
                <div>
                  <span className="font-mono text-ink-muted">{money(a.periodGp)}</span>
                  <span
                    className="block text-[11px] text-ink-faint"
                    title={`Bill ${money(a.billRate)} − Pay ${money(a.payRate)} × LF ${decimalExact(a.loadFactor)} = GP/hr, × ${decimalExact(a.periodHours)} hours`}
                  >
                    {gpFormula(a)}
                  </span>
                </div>
              ) : (
                ''
              )}
            </Td>

            {/* ── Recruiter: Name + role start date ── */}
            <Td group="recruiter" sep className="align-top">
              {rec ? (
                <div>
                  <button
                    type="button"
                    className="hover:text-profit hover:underline"
                    onClick={() => navigate(`recruiter/${rec.userId}`)}
                  >
                    {rec.userName}
                  </button>
                  <span
                    className="block text-[11px] text-ink-faint"
                    title={
                      rec.roleEffectiveDate
                        ? 'When this recruiter started their role'
                        : 'Role start date not set — set it on the Commission Earners screen'
                    }
                  >
                    Role Start:{' '}
                    {rec.roleEffectiveDate
                      ? shortDate(rec.roleEffectiveDate.slice(0, 10))
                      : '\u2014'}
                  </span>
                </div>
              ) : isFirst ? (
                <Dash />
              ) : (
                ''
              )}
            </Td>

            {/* ── Recruiter: Plan ── */}
            <Td group="recruiter" className="align-top">
              {rec ? (
                <PlanCell
                  planId={rec.commissionPlanId}
                  plans={plans}
                  disabled={saving}
                  onPlanChange={(planId) => onPlanChange('recruiting', i, planId)}
                />
              ) : isFirst ? (
                <Dash />
              ) : (
                ''
              )}
            </Td>

            {/* ── Recruiter: % (schedule badge + percentage) ── */}
            <td className={`px-3 py-2 text-right align-top ${G.recruiter}`}>
              {rec ? (
                isEditingRec && editingCell ? (
                  <InlinePctInput
                    value={editingCell.value}
                    onChange={onCellChange}
                    onSave={onCellSave}
                    onCancel={onCellCancel}
                    saving={saving}
                  />
                ) : (
                  <div className="flex flex-col items-end gap-0.5">
                    {rec.schedule ? (
                      <ScheduleBadge
                        schedule={rec.schedule}
                        elevatedPeriodMonths={
                          plans.find((p) => p.id === rec.commissionPlanId)?.elevatedPeriodMonths ??
                          18
                        }
                      />
                    ) : null}
                    <ClickablePct
                      value={pct(rec.share)}
                      warning={recWarning}
                      onClick={() => onCellClick(a.assignmentId, 'recruiting', i, rec.share)}
                    />
                  </div>
                )
              ) : (
                isFirst && <Dash />
              )}
            </td>

            {/* ── Sales: Name + role start date ── */}
            <Td group="sales" sep className="align-top">
              {sal ? (
                <div>
                  <button
                    type="button"
                    className="hover:text-profit hover:underline"
                    onClick={() => navigate(`recruiter/${sal.userId}`)}
                  >
                    {sal.userName}
                  </button>
                  <span
                    className="block text-[11px] text-ink-faint"
                    title={
                      sal.roleEffectiveDate
                        ? 'When this salesperson started their role'
                        : 'Role start date not set — set it on the Commission Earners screen'
                    }
                  >
                    Role Start:{' '}
                    {sal.roleEffectiveDate
                      ? shortDate(sal.roleEffectiveDate.slice(0, 10))
                      : '\u2014'}
                  </span>
                </div>
              ) : isFirst ? (
                <Dash />
              ) : (
                ''
              )}
            </Td>

            {/* ── Sales: Plan ── */}
            <Td group="sales" className="align-top">
              {sal ? (
                <PlanCell
                  planId={sal.commissionPlanId}
                  plans={plans}
                  disabled={saving}
                  onPlanChange={(planId) => onPlanChange('sales', i, planId)}
                />
              ) : isFirst ? (
                <Dash />
              ) : (
                ''
              )}
            </Td>

            {/* ── Sales: % (schedule badge + percentage) ── */}
            <td className={`px-3 py-2 text-right align-top ${G.sales}`}>
              {sal ? (
                isEditingSal && editingCell ? (
                  <InlinePctInput
                    value={editingCell.value}
                    onChange={onCellChange}
                    onSave={onCellSave}
                    onCancel={onCellCancel}
                    saving={saving}
                  />
                ) : (
                  <div className="flex flex-col items-end gap-0.5">
                    {sal.schedule ? (
                      <ScheduleBadge
                        schedule={sal.schedule}
                        elevatedPeriodMonths={
                          plans.find((p) => p.id === sal.commissionPlanId)?.elevatedPeriodMonths ??
                          18
                        }
                      />
                    ) : null}
                    <ClickablePct
                      value={pct(sal.share)}
                      warning={salesWarning}
                      onClick={() => onCellClick(a.assignmentId, 'sales', i, sal.share)}
                    />
                  </div>
                )
              ) : (
                isFirst && <Dash />
              )}
            </td>
          </tr>
        );
      })}
    </>
  );
}

/* ── Clickable percentage ────────────────────────────────────────── */

function ClickablePct({
  value,
  warning,
  onClick,
}: {
  value: number;
  warning: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`rounded px-2 py-0.5 font-mono text-sm transition-colors hover:bg-profit/10 hover:text-profit ${
        warning ? 'font-semibold text-amber-600' : 'text-ink-muted'
      }`}
      onClick={onClick}
      title="Click to edit"
    >
      {String(value)}%
    </button>
  );
}

/* ── Inline percentage input ─────────────────────────────────────── */

function InlinePctInput({
  value,
  onChange,
  onSave,
  onCancel,
  saving,
}: {
  value: string;
  onChange: (v: string) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);

  return (
    <span className="inline-flex items-center gap-1">
      <input
        ref={ref}
        type="number"
        min={1}
        max={100}
        className="w-16 rounded border-2 border-profit bg-surface-default px-2 py-0.5 text-right font-mono text-sm outline-none"
        value={value}
        disabled={saving}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onSave();
          if (e.key === 'Escape') onCancel();
        }}
        onBlur={onSave}
      />
      <span className="text-xs text-ink-muted">%</span>
    </span>
  );
}

/* ── Structural edit rows ────────────────────────────────────────── */

function StructuralEditRows({
  assignment: a,
  draft,
  setDraft,
  recruitersOnly,
  salespeopleOnly,
  saving,
  onSave,
  onCancel,
}: {
  assignment: CreditAssignment;
  draft: StructuralEdit;
  setDraft: (d: StructuralEdit | null) => void;
  recruitersOnly: RecruiterOption[];
  salespeopleOnly: RecruiterOption[];
  saving: boolean;
  onSave: () => void;
  onCancel: () => void;
}) {
  const updatePerson = (lane: 'recruiting' | 'sales', idx: number, userId: string) => {
    const options = lane === 'recruiting' ? recruitersOnly : salespeopleOnly;
    const name = options.find((r) => r.id === userId)?.fullName ?? '';
    const list = [...draft[lane]];
    list[idx] = { ...list[idx]!, userId, userName: name };
    setDraft({ ...draft, [lane]: list });
  };

  const updateShare = (lane: 'recruiting' | 'sales', idx: number, pctVal: number) => {
    const list = [...draft[lane]];
    list[idx] = { ...list[idx]!, share: String(pctVal / 100) };
    setDraft({ ...draft, [lane]: list });
  };

  const addToLane = (lane: 'recruiting' | 'sales') => {
    setDraft({
      ...draft,
      [lane]: [...draft[lane], { userId: '', share: '1', userName: '' }],
    });
  };

  const removeFromLane = (lane: 'recruiting' | 'sales', idx: number) => {
    setDraft({
      ...draft,
      [lane]: draft[lane].filter((_, i) => i !== idx),
    });
  };

  const maxLen = Math.max(draft.recruiting.length, draft.sales.length, 1);
  const recWarning = isNonStandard(draft.recruiting);
  const salesWarning = isNonStandard(draft.sales);

  return (
    <>
      {Array.from({ length: maxLen }, (_, i) => {
        const rec = draft.recruiting[i];
        const sal = draft.sales[i];
        const isFirst = i === 0;

        return (
          <tr
            key={`${a.assignmentId}-struct-${String(i)}`}
            className="border-b border-edge/50 bg-blue-50/40"
          >
            {/* Contractor (name + client) */}
            <td className="sticky left-0 z-[5] px-3 py-2 text-sm text-ink-muted bg-blue-50/40">
              {isFirst ? (
                <div>
                  <span>{a.workerName}</span>
                  <span className="block text-[11px] text-ink-faint">{a.clientName}</span>
                </div>
              ) : (
                ''
              )}
            </td>

            {/* Financials (read-only during structural edit) */}
            <td className={`px-3 py-2 text-right font-mono text-sm text-ink-muted ${SEP_BORDER}`}>
              {isFirst ? money(a.billRate) : ''}
            </td>
            <td className="px-3 py-2 text-right font-mono text-sm text-ink-muted">
              {isFirst ? money(a.payRate) : ''}
            </td>
            <td className="px-3 py-2 text-right text-sm text-ink-muted">
              {isFirst ? (
                <div>
                  <span className="font-mono">{money(a.periodGp)}</span>
                  <span className="block text-[11px] text-ink-faint">{gpFormula(a)}</span>
                </div>
              ) : (
                ''
              )}
            </td>

            {/* Recruiter select */}
            <td className={`px-3 py-2 text-sm ${SEP_BORDER}`}>
              {rec ? (
                <div className="flex items-center gap-1">
                  <select
                    className="max-w-[160px] rounded border border-border-default bg-surface-default px-2 py-1 text-sm"
                    value={rec.userId}
                    onChange={(e) => updatePerson('recruiting', i, e.target.value)}
                  >
                    <option value="">Select…</option>
                    {recruitersOnly.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.fullName}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="text-xs text-red-400 hover:text-red-600"
                    onClick={() => removeFromLane('recruiting', i)}
                    title="Remove"
                  >
                    ✕
                  </button>
                </div>
              ) : (
                ''
              )}
            </td>
            <td className="px-3 py-2 text-center text-[11px] text-ink-faint">—</td>
            <td className="px-3 py-2 text-right">
              {rec ? (
                <span className="inline-flex items-center gap-1">
                  <input
                    type="number"
                    min={1}
                    max={100}
                    className="w-14 rounded border border-border-default bg-surface-default px-2 py-1 text-right font-mono text-sm"
                    value={pct(rec.share)}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      if (!Number.isNaN(v)) updateShare('recruiting', i, v);
                    }}
                  />
                  <span className="text-xs text-ink-muted">%</span>
                </span>
              ) : (
                ''
              )}
            </td>

            {/* Salesperson select */}
            <td className={`px-3 py-2 text-sm ${SEP_BORDER}`}>
              {sal ? (
                <div className="flex items-center gap-1">
                  <select
                    className="max-w-[160px] rounded border border-border-default bg-surface-default px-2 py-1 text-sm"
                    value={sal.userId}
                    onChange={(e) => updatePerson('sales', i, e.target.value)}
                  >
                    <option value="">Select…</option>
                    {salespeopleOnly.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.fullName}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="text-xs text-red-400 hover:text-red-600"
                    onClick={() => removeFromLane('sales', i)}
                    title="Remove"
                  >
                    ✕
                  </button>
                </div>
              ) : (
                ''
              )}
            </td>
            <td className="px-3 py-2 text-center text-[11px] text-ink-faint">—</td>
            <td className="px-3 py-2 text-right">
              {sal ? (
                <span className="inline-flex items-center gap-1">
                  <input
                    type="number"
                    min={1}
                    max={100}
                    className="w-14 rounded border border-border-default bg-surface-default px-2 py-1 text-right font-mono text-sm"
                    value={pct(sal.share)}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      if (!Number.isNaN(v)) updateShare('sales', i, v);
                    }}
                  />
                  <span className="text-xs text-ink-muted">%</span>
                </span>
              ) : (
                ''
              )}
            </td>
          </tr>
        );
      })}

      {/* Add / totals row */}
      <tr className="border-b border-edge/50 bg-blue-50/40">
        <td colSpan={4} />
        <td className="px-3 pb-2 pt-1">
          <button
            type="button"
            className="text-xs text-profit hover:underline"
            onClick={() => addToLane('recruiting')}
          >
            + Add Recruiter
          </button>
        </td>
        <td />
        <td className="px-3 pb-2 pt-1 text-right text-xs text-ink-muted">
          {draft.recruiting.length > 0 && (
            <>
              Total:{' '}
              <span className={recWarning ? 'font-semibold text-amber-600' : ''}>
                {String(Math.round(laneTotal(draft.recruiting) * 100))}%
              </span>
              {recWarning && ' ⚠'}
            </>
          )}
        </td>
        <td className={`px-3 pb-2 pt-1 ${SEP_BORDER}`}>
          <button
            type="button"
            className="text-xs text-profit hover:underline"
            onClick={() => addToLane('sales')}
          >
            + Add Salesperson
          </button>
        </td>
        <td />
        <td className="px-3 pb-2 pt-1 text-right text-xs text-ink-muted">
          {draft.sales.length > 0 && (
            <>
              Total:{' '}
              <span className={salesWarning ? 'font-semibold text-amber-600' : ''}>
                {String(Math.round(laneTotal(draft.sales) * 100))}%
              </span>
              {salesWarning && ' ⚠'}
            </>
          )}
        </td>
      </tr>

      {/* Save / Cancel */}
      <tr className="border-b border-edge bg-blue-50/40">
        <td colSpan={COL_COUNT} className="px-3 pb-2.5 pt-1 text-right">
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              className="rounded bg-profit px-4 py-1.5 text-xs font-medium text-white hover:bg-profit/80 disabled:opacity-50"
              onClick={onSave}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
            <button
              type="button"
              className="rounded border border-border-default px-4 py-1.5 text-xs font-medium text-ink-muted hover:bg-surface-alt"
              onClick={onCancel}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </td>
      </tr>
    </>
  );
}

/* ── Tiny pieces ────────────────────────────────────────────────────── */

function Dash() {
  return <span className="text-ink-faint">—</span>;
}

function PlanSelect({
  value,
  plans,
  disabled,
  onChange,
}: {
  value: string | null;
  plans: { id: string; name: string }[];
  disabled: boolean;
  onChange: (planId: string) => void;
}) {
  return (
    <select
      className="w-full max-w-[10rem] rounded border border-edge bg-surface-base px-1.5 py-0.5 text-[11px] text-ink-muted"
      value={value ?? ''}
      disabled={disabled}
      onChange={(e) => {
        if (e.target.value) onChange(e.target.value);
      }}
    >
      <option value="">Select plan…</option>
      {plans.map((p) => (
        <option key={p.id} value={p.id}>
          {p.name.replace(' Commission', '')}
        </option>
      ))}
    </select>
  );
}

function ScheduleBadge({
  schedule,
  elevatedPeriodMonths,
}: {
  schedule: string;
  elevatedPeriodMonths?: number;
}) {
  const isElevated = schedule === 'elevated';
  const months = elevatedPeriodMonths ?? 18;
  const tooltip = isElevated
    ? `Elevated \u2014 Contractor assigned during first ${String(months)} months`
    : `Standard \u2014 Contractor assigned after first ${String(months)} months`;
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${
        isElevated ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'
      }`}
      title={tooltip}
    >
      {isElevated ? 'Elevated' : 'Standard'}
    </span>
  );
}

/** Plan dropdown cell (just the select) */
function PlanCell({
  planId,
  plans,
  disabled,
  onPlanChange,
}: {
  planId: string | null;
  plans: { id: string; name: string; elevatedPeriodMonths: number }[];
  disabled: boolean;
  onPlanChange: (planId: string) => void;
}) {
  return <PlanSelect value={planId} plans={plans} disabled={disabled} onChange={onPlanChange} />;
}
